import React, { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAccess } from '../../../access/AccessContext'
import { useSessionOperationReady } from '../../../lib/SessionOperationContext'
import { readProgramEvidence, approveProgramEvidence } from '../services/programEvidenceApprovalApi'

function ExactReview({ program, id, actor, guard, onApproved }) {
    const [record, setRecord] = useState(null)
    const [state, setState] = useState('LOADING')
    const [message, setMessage] = useState('')
    const [comments, setComments] = useState('')
    const mounted = useRef(false), intent = useRef(null), locked = useRef(false)
    const heading = useRef(null)
    const receiptKey = `rigo:approval:${program}:${actor}:${id}`
    const sentReceipt = () => { try { return sessionStorage.getItem(receiptKey) === 'sent' } catch { return false } }
    const markReceipt = sent => { try { if (sent) sessionStorage.setItem(receiptKey, 'sent'); else sessionStorage.removeItem(receiptKey) } catch { /* The server transition remains repeat-safe. */ } }
    const accepts = row => row && String(row.id) === String(id) && row.program_reference === program
        && row.acting_reviewer === actor && ['draft', 'approved'].includes(row.status)
    useEffect(() => {
        mounted.current = true
        let active = true
        readProgramEvidence(program, id).then(row => {
            if (!active) return
            if (!accepts(row)) throw new Error('Evidence identity did not match the requested review.')
            setRecord(row)
            if (row.status === 'approved') { markReceipt(false); setState('APPROVED') }
            else if (sentReceipt()) { locked.current = true; setState('UNKNOWN'); setMessage('A prior approval may still be processing. Check status; do not repeat approval.') }
            else setState('REVIEW')
        }).catch(() => { if (active) { setState('UNAVAILABLE'); setMessage('The requested evidence cannot be reviewed. No other snapshot was substituted.') } })
        return () => { active = false; mounted.current = false; intent.current = null }
        // Identity changes remount this review; an old request cannot update it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    useEffect(() => { if (record) heading.current?.focus() }, [record])
    const current = item => mounted.current && intent.current === item && (!guard.isCurrent || guard.isCurrent())
    const confirmed = row => {
        if (!accepts(row) || row.status !== 'approved' || !row.approved_at || !row.approved_by) return false
        markReceipt(false); setRecord(row); setState('APPROVED'); setMessage('Current evidence APPROVED.'); onApproved(); return true
    }
    const reconcile = async (rejected = false) => {
        setState('CHECKING')
        try {
            const row = await readProgramEvidence(program, id)
            if (!mounted.current) return
            if (confirmed(row)) return
            if (rejected === true && accepts(row) && row.status === 'draft') {
                markReceipt(false); locked.current = false; intent.current = null
                setRecord(row); setState('REVIEW')
                setMessage('Approval was rejected. The server confirms DRAFT. Check session, CSRF, access and evidence validity before a deliberate new attempt.')
                return
            }
        } catch { /* A failed read or DRAFT is not proof that an in-flight POST cannot commit. */ }
        if (mounted.current) { setState('UNKNOWN'); setMessage('Approval outcome is not confirmed. Do not approve again. Check authoritative status.') }
    }
    const approve = async () => {
        if (locked.current || !record?.can_approve || record.status !== 'draft') return
        locked.current = true
        const item = { sent: false }; intent.current = item
        const ready = async () => {
            if (!current(item)) return false
            if (!guard() && !await guard.ensureReady?.()) return false
            return current(item) && guard()
        }
        ready.isCurrent = () => current(item) && guard()
        setState('PREPARING'); setMessage('')
        try {
            const row = await approveProgramEvidence(program, id, comments, { ready,
                onSending: () => { item.sent = true; markReceipt(true); setState('SENDING') } })
            if (!current(item)) return
            if (!confirmed(row)) await reconcile()
        } catch (failure) {
            if (!current(item)) return
            if (failure.notSent && !item.sent) {
                locked.current = false; intent.current = null; setState('REVIEW'); setMessage(failure.message)
            } else {
                // These responses are pre-write rejections or an atomic rollback
                // from this endpoint. Transport/5xx failures remain ambiguous.
                const rejected = [400, 403, 404, 405].includes(failure.response?.status)
                setMessage('Checking whether approval was recorded…'); await reconcile(rejected)
            }
        }
    }
    const cancel = () => {
        if (intent.current?.sent) return
        intent.current = null; locked.current = false; setState('REVIEW'); setMessage('Approval cancelled before sending.')
    }
    return <section aria-label="Exact evidence review" aria-busy={['LOADING', 'PREPARING', 'SENDING', 'CHECKING'].includes(state)}>
        <h3 ref={heading} tabIndex={-1}>Review Program Current Evidence — Snapshot #{id}</h3>
        {message && <p role="status">{message}</p>}
        {record && <>
            <dl>{Object.entries({ Program: record.program_reference, 'PortfolioSnapshot ID': record.id,
                Snapshot: record.snapshot_name, Status: record.status.toUpperCase(), 'Analytical evidence ID': record.analytical_evidence_id,
                'Capture UUID': record.capture_event_id, Purpose: record.purpose, Currency: record.currency,
                'Measured at': record.measured_at, EAL: record.expected_annual_loss, P95: record.p95,
                Simulations: record.simulations, Seed: record.seed ?? 'Unavailable', Producer: JSON.stringify(record.producer),
                Provenance: record.provenance, Completeness: record.completeness, 'Acting reviewer': record.acting_reviewer,
                'Approved by': record.approved_by || 'Not approved', 'Approved at': record.approved_at || 'Not approved',
            }).map(([label, value]) => <div key={label}><dt>{label}</dt><dd className="break-words">{value}</dd></div>)}</dl>
            <p>Approval finalizes this analytical evidence and makes eligible evidence available for a separate reassessment. It does not change Program capital authority or create a reassessment.</p>
            {state === 'REVIEW' && <><label>Optional approval comments<textarea value={comments} onChange={e => setComments(e.target.value)} /></label>
                {record.can_approve ? <button type="button" onClick={approve}>Approve Current Evidence</button>
                    : <p>Analytical evidence approval permission is required.</p>}</>}
        </>}
        {state === 'LOADING' && <p role="status">Loading exact evidence…</p>}
        {state === 'PREPARING' && <><p role="status">Preparing approval. Nothing has been sent.</p><button type="button" onClick={cancel}>Cancel unsent approval</button></>}
        {state === 'SENDING' && <p role="status">Recording approval. Do not repeat the action.</p>}
        {state === 'CHECKING' && <p role="status">Checking authoritative approval status…</p>}
        {state === 'UNKNOWN' && <button type="button" onClick={() => reconcile()}>Check approval status</button>}
        {state === 'APPROVED' && <p role="status">Evidence is APPROVED. Reassessment creation remains a separate action.</p>}
    </section>
}

export default function ProgramEvidenceReview({ programReference, onApproved = () => {} }) {
    const access = useAccess(), guard = useSessionOperationReady()
    const [params, setParams] = useSearchParams()
    const id = params.get('evidence_snapshot')
    const [rows, setRows] = useState([]), [error, setError] = useState('')
    const scope = `${programReference}:${access.username}:${guard.generation ?? ''}`
    useEffect(() => {
        let active = true
        setRows([]); setError('')
        readProgramEvidence(programReference).then(data => { if (active) setRows(Array.isArray(data) ? data : []) })
            .catch(() => { if (active) setError('Program evidence discovery is unavailable or access is restricted.') })
        return () => { active = false }
    }, [scope, programReference])
    return <section aria-label="Program evidence approval" className="rounded-xl border bg-white p-5 space-y-3">
        <h2>Program Current Evidence Review and Approval</h2>
        <p>Review exact evidence independently of reassessment creation.</p>
        {error && <p role="alert">{error}</p>}
        <label>Evidence to review<select value={id || ''} onChange={e => { const next = new URLSearchParams(params); if (e.target.value) next.set('evidence_snapshot', e.target.value); else next.delete('evidence_snapshot'); setParams(next) }}>
            <option value="">Select exact evidence</option>
            {id && !rows.some(r => String(r.id) === id) && <option value={id}>Requested snapshot #{id}</option>}
            {rows.map(r => <option key={r.id} value={r.id}>Snapshot #{r.id} · {r.status.toUpperCase()} · {r.snapshot_name}</option>)}
        </select></label>
        {id && <ExactReview key={`${scope}:${id}`} program={programReference} id={id} actor={access.username} guard={guard} onApproved={onApproved} />}
    </section>
}
